import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BasketService, PaymentType } from '@basket';
import { CatalogueService } from '@catalogue';
import { CONFIG } from '@config';
import { CheckoutService } from '@features/checkout';
import { MultiPlanService } from '@features/multi-plan';
import {
  extractPageNameFromUrl,
  GA_CONFIG,
  GACountryCode,
  GAEvent,
  GAJourneyVersion,
  GAService,
  GATag,
} from '@ga';
import { COUNTRY_ISO_NUMBER, CountryIsoNumber, CurrencyCode } from '@i18n';
import { format } from 'date-fns';
import posthog from 'posthog-js';
import { filter } from 'rxjs';
import { GAPrequoteAppliance } from '../../models/ga-prequote-appliance.interface';
import {
  formatSelectedApplianceDetailsToBasketBrandList,
  formatSelectedApplianceDetailsToBasketDateOfPurchaseList,
  formatSelectedApplianceDetailsToBasketProductNameList,
  formatSelectedApplianceDetailsToBasketPurchaseCostList,
} from '../../utils/ga-data.utils';
@Injectable({
  providedIn: 'root',
})
export class GADataService {
  private readonly basketService = inject(BasketService);
  private readonly catalogueService = inject(CatalogueService);
  private readonly checkoutService = inject(CheckoutService);
  private readonly countryIsoNumber = inject(COUNTRY_ISO_NUMBER);
  private readonly config = inject(CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gaConfig = inject(GA_CONFIG);
  private readonly gaService = inject(GAService);
  private readonly multiPlanService = inject(MultiPlanService);
  private readonly router = inject(Router);

  // Private signals and properties
  private lastUrl = '';
  private readonly _journeyVersion = signal<GAJourneyVersion>(
    GAJourneyVersion.Notset,
  );
  private readonly _multiPlanOffer = signal<1 | 0>(0);
  private readonly _offerStatus = signal<1 | 0>(0);

  // Public signals
  readonly journeyVersion = this._journeyVersion.asReadonly();
  readonly multiPlanOffer = this._multiPlanOffer.asReadonly();
  readonly offerStatus = this._offerStatus.asReadonly();

  readonly basketTotal = computed(() =>
    this.basketService._showMultiPlanJourney()
      ? this.multiPlanService.total()
      : this.basketService
          .basketItemQuoteDetailsPaymentOption()
          .subsequentPayment.toFixed(2),
  );

  readonly preQuoteAppliance = computed<GAPrequoteAppliance>(() => {
    const appliance = this.catalogueService.steppedFormAppliance();

    return {
      appliance_code: appliance?.applianceCode,
      appliance_type: appliance?.applianceName,
      appliance_warranty: this.catalogueService.warrantyLengthInYears(),
      brand: this.config.clientName,
      item_brand: this.config.clientName,
      dateOfPurchase: format(
        this.catalogueService.selectedAppliancePurchaseDate(),
        'MMMM yyyy',
      ),
      modelNumber: this.catalogueService.selectedApplianceModelNumber()
        ? 'provided'
        : 'not provided',
      modelNumberDetail: this.catalogueService.selectedApplianceModelNumber(),
      serialNumber: this.catalogueService.selectedApplianceSerialNumber()
        ? 'provided'
        : 'not provided',
      serialNumberDetail: this.catalogueService.selectedApplianceSerialNumber(),
      productName: appliance?.applianceName,
      item_name: `${this.config.clientName} ${appliance?.applianceName}`,
      purchaseCost: this.catalogueService.selectedAppliancePurchasePrice(),
    };
  });

  readonly basketBrand = computed<{ basketBrand: string }>(() => ({
    basketBrand: formatSelectedApplianceDetailsToBasketBrandList(
      this.config.clientBrandCode,
      this.multiPlanService.selectedAppliancesDetails(),
    ),
  }));

  readonly basketDateOfPurchase = computed<{ basketDateOfPurchase: string }>(
    () => ({
      basketDateOfPurchase:
        formatSelectedApplianceDetailsToBasketDateOfPurchaseList(
          this.preQuoteAppliance().dateOfPurchase,
          this.multiPlanService.selectedAppliancesDetails(),
        ),
    }),
  );

  readonly basketProductName = computed<{ basketProductName: string }>(() => ({
    basketProductName: formatSelectedApplianceDetailsToBasketProductNameList(
      this.preQuoteAppliance().appliance_type as string,
      this.multiPlanService.selectedAppliancesDetails(),
    ),
  }));

  readonly basketPurchaseCost = computed<{ basketPurchaseCost: string }>(
    () => ({
      basketPurchaseCost:
        formatSelectedApplianceDetailsToBasketPurchaseCostList(
          this.preQuoteAppliance().purchaseCost,
          this.multiPlanService.selectedAppliancesDetails(),
        ),
    }),
  );

  readonly eventRegistrationStored = computed<GATag>(() => ({
    ...this.preQuoteAppliance(),
    basketApplianceCount:
      this.multiPlanService.selectedAppliancesDetails().length + 1,
    basketId: this.basketService.basket()?.basketId,
    event: 'registrationStored',
    eventLabel: 1,
    registrationsStored: 1,
    journeyType: this.journeyVersion(),
    multiPlanOffer: this.multiPlanOffer(),
    offerStatus: this.offerStatus(),
    offerPrice: this.basketService.basket()
      ? this.basketTotal()
      : 'no quote available for selected appliance',
  }));

  readonly eventAddToCart = computed<GATag>(() => ({
    basketApplianceCount:
      this.multiPlanService.selectedAppliancesDetails().length + 1,
    event: 'add_to_cart',
    eventAction: 'add_to_cart',
    items: [{ ...this.preQuoteAppliance() }],
    journeyType: this.journeyVersion(),
    multiPlanOffer: this.multiPlanOffer(),
    offerStatus: this.offerStatus(),
    price: this.basketTotal(),
  }));

  readonly eventBasketTotal = computed<GATag>(() => ({
    event: 'basket_total',
    eventAction: this.basketTotal(),
    eventCategory: 'basket',
    eventLabel: 1,
    basketApplianceCount:
      this.multiPlanService.selectedAppliancesDetails().length + 1,
    ...this.basketBrand(),
    ...this.basketDateOfPurchase(),
    ...this.basketProductName(),
    ...this.basketPurchaseCost(),
    basketId: this.basketService.basket()?.basketId,
    basketPrice: this.basketTotal(),
    journeyType: this.journeyVersion(),
    multiPlanOffer: this.multiPlanOffer(),
    offerStatus: this.offerStatus(),
  }));

  readonly eventPurchase = computed<GATag>(() => ({
    event: 'purchase',
    ecommerce: {
      transaction_id:
        this.basketService.basket()?.basketId ?? 'basket id not available yet',
      currency:
        this.countryIsoNumber === CountryIsoNumber.GreatBritain
          ? CurrencyCode.PoundSterling
          : CurrencyCode.Euro,
      value: this.basketTotal(),
      items: [
        {
          ...this.preQuoteAppliance(),
          item_id: this.checkoutService.planNumber(),
          price: this.basketTotal(),
          quantity:
            this.multiPlanService.selectedAppliancesDetails().length + 1,
        },
      ],
    },
    basketApplianceCount:
      this.multiPlanService.selectedAppliancesDetails().length + 1,
    journeyType: this.journeyVersion(),
    multiPlanOffer: this.multiPlanOffer(),
    offerStatus: this.offerStatus(),
    paymentType: PaymentType.DirectDebit,
    planCount: this.multiPlanService.selectedAppliancesDetails().length + 1,
    planNumber: this.checkoutService.planNumber(),
    productName: this.preQuoteAppliance().appliance_type,
    schemeCode: this.checkoutService.schemeCode(),
  }));

  // Direct debit details event
  readonly eventDirectDebitDetails = computed<GATag>(() => ({
    eventAction: 'submit form: true',
    eventCategory: 'payment details form',
    eventLabel: 'success',
    basketApplianceCount:
      this.multiPlanService.selectedAppliancesDetails().length + 1,
    items: [{ ...this.preQuoteAppliance() }],
    offerPrice: this.basketTotal(),
    journeyType: this.journeyVersion(),
    multiPlanOffer: this.multiPlanOffer(),
    offerStatus: this.offerStatus(),
  }));

  // Stepped Form Events
  readonly eventGetStarted = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'CTA button',
    eventCategory: `${this.config.clientName} landing page`,
    eventLabel: `Let's get started`,
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventToggleMoreAppliances = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'expand',
    eventCategory: 'appliance details form',
    eventLabel: 'Show more appliances',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventToggleFewerAppliances = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'collapse',
    eventCategory: 'appliance details form',
    eventLabel: 'Show fewer appliances',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventAppliance = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'applianceCategoryCode',
    eventCategory: 'appliance details form',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventApplianceType = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'applianceCode',
    eventCategory: 'appliance details form',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventPurchasePrice = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'purchasePrice',
    eventCategory: 'appliance details form',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventPurchaseDate = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'purchaseDate',
    eventCategory: 'appliance details form',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventGuaranteeLength = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'warranty',
    eventCategory: 'appliance details form',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventModelNumber = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'modelNumber',
    eventCategory: 'appliance details form',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventSerialNumber = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'serialNumber',
    eventCategory: 'appliance details form',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventModelSerialNumberSkipThisStep = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'Skip this step',
    eventCategory: 'Continue button',
    eventLabel: 'Do you have the model and serial number?',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventModelSerialNumberContinue = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'continue',
    eventCategory: 'Continue button',
    eventLabel: 'Do you have the model and serial number?',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventFirstName = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'firstName',
    eventCategory: 'appliance details form',
    eventLabel: 'COMPLETE',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventLastName = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'surname',
    eventCategory: 'appliance details form',
    eventLabel: 'COMPLETE',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventAddress = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'address',
    eventCategory: 'appliance details form',
    eventLabel: 'COMPLETE',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  readonly eventSteppedFormSubmit = computed<GATag>(() => ({
    category: this.gaConfig.category,
    event: GAEvent.GenericEvent,
    eventAction: 'Continue',
    eventCategory: 'Continue button',
    eventLabel: 'Appliance details form',
    type: this.gaConfig.type,
    version: this.gaConfig.version,
  }));

  // Setters
  setJourneyVersion(journeyVersion: GAJourneyVersion): void {
    this._journeyVersion.set(journeyVersion);
  }

  setMultiPlanOffer(multiPlanOffer: 1 | 0): void {
    this._multiPlanOffer.set(multiPlanOffer);
  }

  setOfferStatus(offerStatus: 1 | 0): void {
    this._offerStatus.set(offerStatus);
  }

  // Methods
  trackPageViews(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      )
      .subscribe((e) => {
        if (e.urlAfterRedirects === this.lastUrl) {
          return;
        }
        this.lastUrl = e.urlAfterRedirects;
        const pagename = extractPageNameFromUrl();
        this.gaService.pageView({
          journeyType: this._journeyVersion(),
          multiPlanOffer: this._multiPlanOffer(),
          offerStatus: this._offerStatus(),
          pagename,
        });

        posthog.capture(GAEvent.PageView, {
          event: GAEvent.PageView,
          pagename,
          brand: this.config.clientBrandCode,
          domainId: this.config.domainId,
          country:
            this.countryIsoNumber === CountryIsoNumber.GreatBritain
              ? GACountryCode.GreatBritain
              : GACountryCode.Ireland,
          type: this.gaConfig.type,
          category: this.gaConfig.category,
          version: this.gaConfig.version,
          multiPlanOffer: this._multiPlanOffer(),
          offerStatus: this._offerStatus(),
        });
      });
  }
}
